import {
  createPublicClient,
  createWalletClient,
  formatUnits,
  http,
  maxUint256,
  type Address,
  type PublicClient,
} from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { arbitrum, arbitrumSepolia } from 'viem/chains';
import { ERC20_ABI } from './internal/erc20.js';
import { TRADING_ABI } from './internal/contract.js';

/** `delegations()` returns this when no delegate is registered. */
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
import { validateBuilderFeeBps } from './internal/builder-fee.js';
import { buildTrade, buildBuilderFee } from './internal/open-builder.js';
import { parseUsdc, parsePrice } from './internal/decimal.js';
import {
  validateCloseParams,
  validateModifyParams,
} from './internal/validation.js';
import { getNetworkConfig } from './internal/contracts.js';
import { reportTradeSubmission } from './internal/attribution.js';
import { DEFAULT_SLIPPAGE_PERCENTAGE, MAX_COLLATERAL_USD } from './internal/precision.js';
import { DEFAULT_BUILDER_API_URL } from './data/config.js';
import { OrderType, CancelOrderType } from './types.js';
import type {
  OnboardingStatus,
  OnboardingStep,
  OpenTradeParams,
  CloseTradeParams,
  CancelOrderParams,
  ModifyOrderParams,
  UpdateCollateralParams,
  AllowanceStatus,
  Balances,
  SubmissionResult,
  BuiltTxRequest,
  EncodedTx,
} from './types.js';
import type {
  ClientMode,
  OstiumClientConfig,
  SelfSelfParams,
  SelfGaslessParams,
  DelegatedSelfParams,
  DelegatedGaslessParams,
} from './config.js';
import {
  DEFAULT_PIMLICO_URL,
  DEFAULT_PIMLICO_URL_TESTNET,
  hasPrivateKey,
  resolveMode,
} from './config.js';
import { OstiumError, OstiumErrorCode } from './errors.js';
import { createSigner, type SignerStrategy } from './signer/index.js';
import { createSubmitter, type SubmissionStrategy } from './submitter/index.js';
import { OstiumSubgraphClient } from './data/client.js';
import { OstiumAccountUpdatesStream, OstiumPositionUpdatesStream } from './data/client.js';
import type {
  PairsResponse,
  AllPricesResponse,
  OpenPositionsResponse,
  Fill,
  Order,
  OpenOrder,
  GetPairsParams,
  GetOrdersParams,
  GetBuilderOrdersParams,
  GetUserParams,
  GetOpenPositionsParams,
  GetFillsParams,
  GetFillsByTimeParams,
  GetSimSlippageParams,
  SimSlippageByPairId,
  GetSimOrderbookParams,
  PreviewOpenTradeParams,
  PreviewOpenTradeResult,
  SimOrderbookResponse,
  Candle,
  GetCandlesParams,
  StreamAccountUpdatesParams,
} from './data/types.js';
import { OstiumPriceStream } from './data/internal/pricing.js';
import {
  encodeOpenTrade,
  encodeCloseTradeMarket,
  encodeCancelLimitOrder,
  encodeCloseTradeMarketTimeout,
  encodeOpenTradeMarketTimeout,
  encodeUpdateOpenLimitOrder,
  encodeUpdateTp,
  encodeUpdateSl,
  encodeTopUpCollateral,
  encodeRemoveCollateral,
  encodeSetDelegate,
  encodeRemoveDelegate,
  encodeUsdcApprove,
} from './encoder.js';

const HEX64_RE = /^0x[0-9a-fA-F]{64}$/;
const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

function validateConfig(config: OstiumClientConfig): void {
  if (config.privateKey && !HEX64_RE.test(config.privateKey)) {
    throw new OstiumError(
      'privateKey must be a 64-character hex string prefixed with 0x',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }
  for (const [label, value] of [
    ['traderAddress', config.traderAddress],
    ['delegateAddress', config.delegateAddress],
    ['safeAddress', config.safeAddress],
  ] as const) {
    if (value && !ADDRESS_RE.test(value)) {
      throw new OstiumError(
        `${label} must be a valid 42-character Ethereum address (0x + 40 hex chars)`,
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
  }
  if (config.slippageBps !== undefined && (config.slippageBps < 0 || config.slippageBps > 500)) {
    throw new OstiumError(
      'slippageBps must be between 0 and 500 (0%–5%)',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }
  if (config.builder !== undefined) {
    const feeError = validateBuilderFeeBps(config.builder.feeBps);
    if (feeError) {
      throw new OstiumError(feeError, OstiumErrorCode.INVALID_CONFIG);
    }
  }

  let mode: ClientMode;
  try {
    mode = resolveMode(config);
  } catch {
    throw new OstiumError(
      'Could not infer client mode from config. Provide the required addresses for the selected factory.',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }

  if (mode === 'self-self' && !config.privateKey && !config.traderAddress) {
    throw new OstiumError(
      'Self + Self mode requires traderPrivateKey for submission or traderAddress for build-only usage.',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }
  if (mode === 'self-gasless' && !config.privateKey && (!config.traderAddress || !config.safeAddress)) {
    throw new OstiumError(
      'Self + Gasless build-only mode requires traderAddress and safeAddress.',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }
  if (mode === 'delegated-self' && (!config.traderAddress || (!config.privateKey && !config.delegateAddress))) {
    throw new OstiumError(
      'Delegated + Self mode requires traderAddress and either delegatePrivateKey or delegateAddress.',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }
  if (
    mode === 'delegated-gasless' &&
    (!config.traderAddress || (!config.privateKey && (!config.delegateAddress || !config.safeAddress)))
  ) {
    throw new OstiumError(
      'Delegated + Gasless mode requires traderAddress and either delegatePrivateKey or both delegateAddress and safeAddress.',
      OstiumErrorCode.INVALID_CONFIG,
    );
  }
}

interface ContractAddresses {
  trading: Address;
  tradingStorage: Address;
  usdc: Address;
  tradingCallbacks: Address;
}

interface ClientInternals {
  /** Builder API base URL — used for fire-and-forget trade attribution. */
  builderApiUrl: string;
  /** Undefined in read-only mode. */
  signer?: SignerStrategy;
  /** Undefined in read-only mode. */
  submitter?: SubmissionStrategy;
  traderAddress?: Address;
  effectiveSender?: Address;
  contracts: ContractAddresses;
  publicClient: PublicClient;
  defaultSlippageBps: number;
  delegated: boolean;
  gasless: boolean;
  /** True when in Self + Gasless mode: EOA is trader, Safe is delegate/submitter. */
  selfGasless: boolean;
  /**
   * Direct EOA submitter for Self + Gasless mode.
   * Used by setupGaslessDelegation() and approveUsdc() — both must be signed
   * by the EOA (not via the Safe delegate).
   */
  eoaSubmit?: (encoded: EncodedTx) => Promise<SubmissionResult>;
  builderAddress?: Address;
  builderFeeBps?: number;
  subgraph: OstiumSubgraphClient;
}

export class OstiumClient {
  private readonly builderApiUrl: string;
  private readonly signer?: SignerStrategy;
  private readonly submitter?: SubmissionStrategy;
  private readonly traderAddress?: Address;
  private readonly effectiveSender?: Address;
  private readonly contracts: ContractAddresses;
  private readonly publicClient: PublicClient;
  private readonly defaultSlippageBps: number;
  private readonly delegated: boolean;
  private readonly gasless: boolean;
  private readonly selfGasless: boolean;
  private readonly eoaSubmit?: (encoded: EncodedTx) => Promise<SubmissionResult>;
  private readonly builderAddress?: Address;
  private readonly builderFeeBps?: number;
  /** Internal subgraph client backing all read methods (`getPairs`, `getOpenPositions`, …). */
  readonly subgraph: OstiumSubgraphClient;

  private constructor(internals: ClientInternals) {
    this.builderApiUrl = internals.builderApiUrl;
    this.signer = internals.signer;
    this.submitter = internals.submitter;
    this.traderAddress = internals.traderAddress;
    this.effectiveSender = internals.effectiveSender;
    this.contracts = internals.contracts;
    this.publicClient = internals.publicClient;
    this.defaultSlippageBps = internals.defaultSlippageBps;
    this.delegated = internals.delegated;
    this.gasless = internals.gasless;
    this.selfGasless = internals.selfGasless;
    this.eoaSubmit = internals.eoaSubmit;
    this.builderAddress = internals.builderAddress;
    this.builderFeeBps = internals.builderFeeBps;
    this.subgraph = internals.subgraph;
  }

  /**
   * **Self + Self** — your EOA signs every transaction and pays gas directly.
   *
   * Use this when you want the simplest possible setup: one key, no smart accounts,
   * no delegation. Your EOA holds USDC and owns all positions.
   *
   * ```ts
   * const client = await OstiumClient.createSelfAndSelf({
   *   traderPrivateKey: '0x...',
   *   rpcUrl: 'https://arb-mainnet.g.alchemy.com/v2/...',
   * });
   * await client.openTrade({ ... });
   * ```
  */
  static async createSelfAndSelf(params: SelfSelfParams): Promise<OstiumClient> {
    const base = {
      mode: 'self-self' as const,
      testnet: params.testnet,
      slippageBps: params.slippageBps,
      builder: params.builder,
      subgraphUrl: params.subgraphUrl,
      builderApiUrl: params.builderApiUrl,
      alchemyApiKey: params.alchemyApiKey,
      rpcUrl: params.rpcUrl,
    };
    return OstiumClient._fromConfig(
      'traderPrivateKey' in params
        ? { ...base, privateKey: params.traderPrivateKey }
        : { ...base, traderAddress: params.traderAddress },
    );
  }

  /**
   * **Self + Gasless** — your EOA owns everything; a Safe relays trades for free.
   *
   * Your EOA holds USDC and owns all positions. A Safe smart account is derived
   * deterministically from your private key and registered as your on-chain delegate.
   * After a one-time setup (approve USDC + `setupGaslessDelegation()`), all trades
   * are submitted as sponsored UserOperations — no ETH required.
   *
   * ```ts
   * const client = await OstiumClient.createSelfAndGasless({
   *   traderPrivateKey: '0x...',
   *   pimlicoUrl: 'https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=42161',
   * });
   * // One-time setup — EOA pays gas once for each:
   * await client.approveUsdc('max');
   * await client.setupGaslessDelegation();
   * // All subsequent trading is gasless:
   * await client.openTrade({ ... });
   * ```
   */
  static async createSelfAndGasless(params: SelfGaslessParams): Promise<OstiumClient> {
    const defaultUrl = params.testnet ? DEFAULT_PIMLICO_URL_TESTNET : DEFAULT_PIMLICO_URL;
    const base = {
      mode: 'self-gasless' as const,
      rpcUrl: params.rpcUrl,
      testnet: params.testnet,
      slippageBps: params.slippageBps,
      builder: params.builder,
      subgraphUrl: params.subgraphUrl,
      builderApiUrl: params.builderApiUrl,
      alchemyApiKey: params.alchemyApiKey,
    };
    return OstiumClient._fromConfig(
      'traderPrivateKey' in params
        ? {
            ...base,
            privateKey: params.traderPrivateKey,
            safeAddress: params.safeAddress,
            pimlicoUrl: params.pimlicoUrl ?? defaultUrl,
            sponsorshipPolicyId: params.sponsorshipPolicyId,
          }
        : {
            ...base,
            traderAddress: params.traderAddress,
            safeAddress: params.safeAddress,
          },
    );
  }

  /**
   * **Delegated + Self** — a delegate EOA signs and pays gas on behalf of a trader address.
   *
   * The trader retains custody of USDC and positions. The delegate only needs ETH
   * for gas. Every trade is wrapped in `delegatedAction(traderAddress, callData)`.
   * The trader must call `setDelegate(delegateAddress)` once before this client
   * can submit trades.
   *
   * ```ts
   * const client = await OstiumClient.createDelegatedAndSelf({
   *   delegatePrivateKey: '0xDelegateKey',
   *   traderAddress: '0xTraderAddress',
   *   rpcUrl: 'https://arb-mainnet.g.alchemy.com/v2/...',
   * });
   * await client.openTrade({ ... });
   * ```
   */
  static async createDelegatedAndSelf(params: DelegatedSelfParams): Promise<OstiumClient> {
    const base = {
      mode: 'delegated-self' as const,
      traderAddress: params.traderAddress,
      rpcUrl: params.rpcUrl,
      testnet: params.testnet,
      slippageBps: params.slippageBps,
      builder: params.builder,
      subgraphUrl: params.subgraphUrl,
      builderApiUrl: params.builderApiUrl,
      alchemyApiKey: params.alchemyApiKey,
    };
    return OstiumClient._fromConfig(
      'delegatePrivateKey' in params
        ? { ...base, privateKey: params.delegatePrivateKey }
        : { ...base, delegateAddress: params.delegateAddress },
    );
  }

  /**
   * **Delegated + Gasless** — a Safe derived from the delegate key submits sponsored
   * UserOperations on behalf of the trader. No ETH needed after setup.
   *
   * The trader retains custody of USDC and positions. The delegate's Safe submits
   * trades as UserOperations via Pimlico. The trader must call `setDelegate(safeAddress)`
   * once — use `client.getSmartAccountAddress()` to retrieve the Safe address.
   *
   * ```ts
   * const client = await OstiumClient.createDelegatedAndGasless({
   *   delegatePrivateKey: '0xDelegateKey',
   *   traderAddress: '0xTraderAddress',
   *   pimlicoUrl: 'https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=42161',
   * });
   * await client.openTrade({ ... }); // gasless delegatedAction UserOp
   * ```
   */
  static async createDelegatedAndGasless(params: DelegatedGaslessParams): Promise<OstiumClient> {
    const defaultUrl = params.testnet ? DEFAULT_PIMLICO_URL_TESTNET : DEFAULT_PIMLICO_URL;
    const base = {
      mode: 'delegated-gasless' as const,
      traderAddress: params.traderAddress,
      rpcUrl: params.rpcUrl,
      testnet: params.testnet,
      slippageBps: params.slippageBps,
      builder: params.builder,
      subgraphUrl: params.subgraphUrl,
      builderApiUrl: params.builderApiUrl,
      alchemyApiKey: params.alchemyApiKey,
    };
    return OstiumClient._fromConfig(
      'delegatePrivateKey' in params
        ? {
            ...base,
            privateKey: params.delegatePrivateKey,
            safeAddress: params.safeAddress,
            pimlicoUrl: params.pimlicoUrl ?? defaultUrl,
            sponsorshipPolicyId: params.sponsorshipPolicyId,
          }
        : {
            ...base,
            delegateAddress: params.delegateAddress,
            safeAddress: params.safeAddress,
          },
    );
  }

  /**
   * **Read-only client** — no signer, no submitter, no privateKey required.
   *
   * All read methods (`getPairs`, `getOpenPositions`, `getFills`, `getBalances`, …)
   * are available. Methods that take an optional `user` argument require it to be
   * passed explicitly. Calling any write method throws `INVALID_CONFIG`.
   *
   * ```ts
   * const reader = await OstiumClient.createReadOnly();
   * const { pairs } = await reader.getPairs();
   * const balances = await reader.getBalances('0xTrader...');
   * const positions = await reader.getOpenPositions({ user: '0xTrader...' });
   * ```
   */
  static async createReadOnly(params: {
    /** Arbitrum RPC URL. Defaults to the chain's public RPC. */
    rpcUrl?: string;
    /** Use Arbitrum Sepolia testnet. Defaults to false (mainnet). */
    testnet?: boolean;
    /** Override the subgraph endpoint. */
    subgraphUrl?: string;
    /** Override the builder API base URL (prices, OHLC, WebSocket stream). */
    builderApiUrl?: string;
    /** Alchemy API key used by `streamAccountUpdates()`. */
    alchemyApiKey?: string;
  } = {}): Promise<OstiumClient> {
    const testnet = params.testnet ?? false;
    const networkConfig = getNetworkConfig(testnet);
    const chain = testnet ? arbitrumSepolia : arbitrum;
    const rpcUrl = params.rpcUrl ?? chain.rpcUrls.default.http[0];

    const contracts: ContractAddresses = {
      trading:        networkConfig.contracts.trading,
      tradingStorage: networkConfig.contracts.tradingStorage,
      usdc:           networkConfig.contracts.usdc,
      tradingCallbacks: networkConfig.contracts.tradingCallbacks,
    };

    const publicClient = createPublicClient({ chain, transport: http(rpcUrl) });

    const subgraph = await OstiumSubgraphClient.create({
      testnet,
      endpoint:      params.subgraphUrl,
      builderApiUrl: params.builderApiUrl,
      alchemyApiKey: params.alchemyApiKey,
    });

    return new OstiumClient({
      builderApiUrl: params.builderApiUrl ?? DEFAULT_BUILDER_API_URL,
      contracts,
      publicClient,
      defaultSlippageBps: Math.round(DEFAULT_SLIPPAGE_PERCENTAGE * 100),
      delegated:   false,
      gasless:     false,
      selfGasless: false,
      subgraph,
    });
  }

  /** Internal factory shared by all public constructors. */
  private static async _fromConfig(config: OstiumClientConfig): Promise<OstiumClient> {
    validateConfig(config);

    const mode = resolveMode(config);
    const testnet = config.testnet ?? false;
    const networkConfig = getNetworkConfig(testnet);
    const chain = testnet ? arbitrumSepolia : arbitrum;
    const defaultSlippageBps =
      config.slippageBps ?? Math.round(DEFAULT_SLIPPAGE_PERCENTAGE * 100);

    const contracts: ContractAddresses = {
      trading:        networkConfig.contracts.trading,
      tradingStorage: networkConfig.contracts.tradingStorage,
      usdc:           networkConfig.contracts.usdc,
      tradingCallbacks: networkConfig.contracts.tradingCallbacks,
    };

    const selfGasless = mode === 'self-gasless';

    let traderAddress: Address;
    if (mode === 'self-self' || mode === 'self-gasless') {
      traderAddress = config.traderAddress ?? privateKeyToAccount(config.privateKey!).address;
    } else {
      traderAddress = config.traderAddress!;
    }

    const signer = createSigner(mode, traderAddress);

    let submitter: SubmissionStrategy | undefined;
    let effectiveSender: Address;
    if (hasPrivateKey(config)) {
      submitter = await createSubmitter(config, testnet, mode);
      effectiveSender = submitter.effectiveSender;
    } else if (mode === 'self-self') {
      effectiveSender = traderAddress;
    } else if (mode === 'delegated-self') {
      effectiveSender = config.delegateAddress!;
    } else {
      effectiveSender = config.safeAddress!;
    }

    const publicRpc = testnet
      ? 'https://sepolia-rollup.arbitrum.io/rpc'
      : 'https://arb1.arbitrum.io/rpc';
    // Chain RPC for reads/simulation. Gasless Pimlico URL is bundler-only
    const rpcUrl = config.rpcUrl ?? publicRpc;

    if (hasPrivateKey(config) && mode !== 'self-gasless' && mode !== 'delegated-gasless' && !config.rpcUrl) {
      throw new OstiumError(
        'rpcUrl is required for self-submission mode',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }

    const publicClient = createPublicClient({ chain, transport: http(rpcUrl) });

    // Self + Gasless: approveUsdc() and setupGaslessDelegation() must be signed
    // by the EOA directly (not via the Safe). Captured in a closure so the
    // private key is not stored on the instance.
    // Pimlico's bundler URL rejects eth_sendRawTransaction, so use a standard RPC here.
    let eoaSubmit: ((encoded: EncodedTx) => Promise<SubmissionResult>) | undefined;
    if (selfGasless && hasPrivateKey(config)) {
      const eoaAccount = privateKeyToAccount(config.privateKey);
      const eoaRpcUrl = config.rpcUrl ?? publicRpc;
      const eoaWalletClient = createWalletClient({ account: eoaAccount, chain, transport: http(eoaRpcUrl) });
      eoaSubmit = async (encoded: EncodedTx) => {
        const txHash = await eoaWalletClient.sendTransaction({
          account: eoaAccount,
          to:      encoded.to,
          data:    encoded.data,
          value:   encoded.value,
        });
        return { txHash };
      };
    }

    const subgraph = await OstiumSubgraphClient.create({
      testnet,
      endpoint:      config.subgraphUrl,
      builderApiUrl: config.builderApiUrl,
      alchemyApiKey: config.alchemyApiKey,
    });

    return new OstiumClient({
      builderApiUrl: config.builderApiUrl ?? DEFAULT_BUILDER_API_URL,
      signer,
      submitter,
      traderAddress,
      effectiveSender,
      contracts,
      publicClient,
      defaultSlippageBps,
      delegated:      mode === 'delegated-self' || mode === 'delegated-gasless',
      gasless:        mode === 'self-gasless' || mode === 'delegated-gasless',
      selfGasless,
      eoaSubmit,
      builderAddress: config.builder?.address,
      builderFeeBps:  config.builder?.feeBps,
      subgraph,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Getters
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * The on-chain address that owns trades and USDC allowances.
   *
   * - Self + Self / Delegated modes: trader's EOA or configured traderAddress.
   * - Self + Gasless: EOA derived from privateKey (NOT the Safe — USDC lives here).
   */
  getTraderAddress(): Address {
    if (!this.traderAddress) {
      throw new OstiumError(
        'No connected trader — pass `user` explicitly or use one of the createSelfAnd*/createDelegatedAnd* factories.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    return this.traderAddress;
  }

  /** True when the client cannot submit transactions directly. */
  isReadOnly(): boolean {
    return !this.submitter;
  }

  /** True when the client can build mode-correct unsigned transaction requests. */
  canBuildTransactions(): boolean {
    return !!this.signer && !!this.effectiveSender;
  }

  /** True when the client has the credentials needed for SDK-managed submission. */
  canSubmitTransactions(): boolean {
    return !!this.submitter;
  }

  /**
   * Returns the Safe smart-account address used for gasless submission.
   * Returns undefined when not in gasless mode.
   *
   * - Self + Gasless:      Safe that submits on behalf of the EOA (the delegate).
   *                        Pass this address to setupGaslessDelegation().
   * - Delegated + Gasless: delegate's Safe address.
   * - Self + Self / Delegated + Self: returns undefined.
   */
  getSmartAccountAddress(): Address | undefined {
    return this.gasless ? this.effectiveSender : undefined;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Self + Gasless setup
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * One-time setup for Self + Gasless mode.
   *
   * Registers the Safe smart account as a delegate on the trading contract so
   * the Safe can submit gasless trades on behalf of your EOA. The EOA pays gas
   * for this single transaction; all subsequent trading is gasless.
   *
   * Only callable in Self + Gasless mode. Throws INVALID_CONFIG otherwise.
   *
   * @example
   * ```ts
   * const client = await OstiumClient.create({ privateKey: '0x...', pimlicoUrl: 'https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=42161' });
   * await client.approveUsdc('max');         // EOA approves USDC (gas, one-time)
   * await client.setupGaslessDelegation();   // EOA sets Safe as delegate (gas, one-time)
   * // From here: all trading is gasless
   * await client.openTrade({ ... });
   * ```
   */
  async setupGaslessDelegation(): Promise<SubmissionResult> {
    return this.submitDirectEoa(this.getSetupGaslessDelegationEncoded());
  }

  getSetupGaslessDelegationTx(): BuiltTxRequest {
    return this.buildDirectEoaTx(this.getSetupGaslessDelegationEncoded());
  }

  // ─────────────────────────────────────────────────────────────────────────
  // USDC helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Check whether the trader's USDC allowance covers the required amount.
   * The allowance is always checked against signer.traderAddress (the EOA in
   * Self + Gasless mode, the Safe in Delegated + Gasless).
   */
  async checkUsdcAllowance(requiredUsd: string): Promise<AllowanceStatus> {
    const required = parseUsdc(requiredUsd);
    const current = await this.publicClient.readContract({
      address: this.contracts.usdc,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [this.getTraderAddress(), this.contracts.tradingStorage],
    });
    return { current, required, sufficient: current >= required };
  }

  /**
   * USDC and ETH balances plus the USDC allowance to TradingStorage.
   * All three values are decimal strings so large values (e.g. max USDC approval)
   * are preserved exactly. Parse with `parseFloat` / `Number` only for display.
   *
   * In read-only mode, pass `user` explicitly. With a connected trader, omit it
   * to read the connected address.
   */
  async getBalances(user?: Address): Promise<Balances> {
    const trader = user ?? this.getTraderAddress();
    const [usdcRaw, ethRaw, allowanceRaw] = await Promise.all([
      this.publicClient.readContract({
        address: this.contracts.usdc,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [trader],
      }),
      this.publicClient.getBalance({ address: trader }),
      this.publicClient.readContract({
        address: this.contracts.usdc,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [trader, this.contracts.tradingStorage],
      }),
    ]);

    return {
      usdc:      formatUnits(usdcRaw as bigint, 6),
      eth:       formatUnits(ethRaw, 18),
      allowance: formatUnits(allowanceRaw as bigint, 6),
    };
  }

  /**
   * Approve USDC spending by the TradingStorage contract.
   *
   * - Self + Self: submitted gaslessly from EOA.
   * - Self + Gasless: submitted directly from EOA (the delegate cannot approve
   *   on behalf of the trader). The EOA pays gas for this one-time operation.
   * - Delegated modes: throws — the trader must approve from their own account.
   *
   * @param amount USD amount as decimal string (e.g. "1000"), or "max" for MaxUint256.
   */
  async approveUsdc(amount: string): Promise<SubmissionResult> {
    const encoded = this.getApproveUsdcEncoded(amount);
    if (this.selfGasless) {
      return this.submitDirectEoa(encoded);
    }
    return this.submitPrepared(encoded);
  }

  getApproveUsdcTx(amount: string): BuiltTxRequest {
    return this.buildDirectEoaTx(this.getApproveUsdcEncoded(amount));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Onboarding
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * What still stands between this trader and their first trade — USDC
   * approval, delegate registration, gasless setup — with the transaction
   * that clears each one.
   *
   * Onboarding is otherwise spread across three separate tx-builders and
   * three sections of BUILDER.md, with no way to ask "what is left?". This
   * answers that in one call, so a UI can render a checklist:
   *
   * ```ts
   * const status = await client.getOnboardingStatus({ requiredUsd: '1000' });
   * for (const step of status.steps) {
   *   console.log(step.description);
   *   await wallet.sendTransaction(step.tx);   // trader signs from their own account
   * }
   * ```
   *
   * Every step is a transaction the **trader** must send themselves: a
   * delegate can neither approve USDC nor register itself.
   *
   * @param params.requiredUsd Collateral the trader intends to trade with.
   *                           Defaults to `"1"` — enough to prove an approval
   *                           exists at all.
   */
  async getOnboardingStatus(params: { requiredUsd?: string } = {}): Promise<OnboardingStatus> {
    const requiredUsd = params.requiredUsd ?? '1';
    // Parsed up front so a malformed amount fails loudly here rather than
    // silently becoming NaN and reporting the trader as ready.
    let requiredRaw: bigint;
    try {
      requiredRaw = parseUsdc(requiredUsd);
    } catch {
      throw new OstiumError(
        `getOnboardingStatus: requiredUsd must be a decimal USD string (received "${requiredUsd}")`,
        OstiumErrorCode.VALIDATION_FAILED,
      );
    }
    if (requiredRaw < 0n) {
      throw new OstiumError(
        `getOnboardingStatus: requiredUsd must not be negative (received "${requiredUsd}")`,
        OstiumErrorCode.VALIDATION_FAILED,
      );
    }

    const trader = this.getTraderAddress();

    const [balances, currentDelegateRaw] = await Promise.all([
      this.getBalances(trader),
      this.publicClient.readContract({
        address: this.contracts.trading,
        abi: TRADING_ABI,
        functionName: 'delegations',
        args: [trader],
      }) as Promise<Address>,
    ]);

    const currentDelegate =
      currentDelegateRaw && currentDelegateRaw !== ZERO_ADDRESS ? currentDelegateRaw : undefined;

    // Compared as 6-decimal integers: a max approval is far beyond the range
    // where Number() is exact.
    const needsApproval = parseUsdc(balances.allowance) < requiredRaw;
    // Sufficient allowance with no USDC is not "ready" — the open still fails.
    const needsFunding = parseUsdc(balances.usdc) < requiredRaw;

    // The address that must be registered as the trader's delegate, if any:
    // the smart account in Self + Gasless, the delegate EOA in delegated modes.
    const expectedDelegate = this.delegated || this.selfGasless ? this.effectiveSender : undefined;
    const delegateMismatch =
      expectedDelegate !== undefined &&
      currentDelegate?.toLowerCase() !== expectedDelegate.toLowerCase();

    const needsGaslessSetup = this.selfGasless && delegateMismatch;
    const needsDelegate = !this.selfGasless && delegateMismatch;

    const steps: OnboardingStep[] = [];
    if (needsApproval) {
      steps.push({
        kind: 'approveUsdc',
        description: `Approve USDC spending (current allowance ${balances.allowance}, need ${requiredUsd}).`,
        // Encoded directly rather than through getApproveUsdcTx(), which
        // refuses delegated clients — yet a delegated integration is exactly
        // where a UI needs this transaction to hand to the trader.
        tx: this.buildDirectEoaTx(
          encodeUsdcApprove(this.contracts.tradingStorage, requiredRaw, this.contracts.usdc),
        ),
      });
    }
    if (needsGaslessSetup) {
      steps.push({
        kind: 'setupGaslessDelegation',
        description: `Authorise the gasless smart account ${expectedDelegate} to trade on your behalf.`,
        tx: this.getSetupGaslessDelegationTx(),
      });
    }
    if (needsDelegate && expectedDelegate) {
      steps.push({
        kind: 'setDelegate',
        description: `Register ${expectedDelegate} as your delegate.`,
        // Direct, not getSetDelegateTx(): that wraps the call in delegatedAction
        // so the *existing* delegate signs it. There is no delegate yet — that
        // is the point of the step — so it must come from the trader.
        tx: this.buildDirectEoaTx(encodeSetDelegate(expectedDelegate, this.contracts.trading)),
      });
    }

    return {
      ready: steps.length === 0 && !needsFunding,
      needsApproval,
      needsFunding,
      needsDelegate,
      needsGaslessSetup,
      steps,
      allowance: balances.allowance,
      usdcBalance: balances.usdc,
      ethBalance: balances.eth,
      currentDelegate,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Delegation helpers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Set a delegate on the trading contract.
   *
   * - Self + Self: EOA directly registers a delegate.
   * - Self + Gasless: NOT recommended — use setupGaslessDelegation() instead.
   * - Delegated modes: wrapped in delegatedAction so the existing delegate can
   *   update the trader's delegation on their behalf.
   */
  async setDelegate(delegateAddress: Address): Promise<SubmissionResult> {
    return this.submitPrepared(this.getSetDelegateEncoded(delegateAddress));
  }

  getSetDelegateTx(delegateAddress: Address): BuiltTxRequest {
    return this.buildPreparedTx(this.getSetDelegateEncoded(delegateAddress));
  }

  /**
   * Remove the current delegate on the trading contract.
   * Follows the same delegation-wrapping rules as setDelegate().
   */
  async removeDelegate(): Promise<SubmissionResult> {
    return this.submitPrepared(this.getRemoveDelegateEncoded());
  }

  getRemoveDelegateTx(): BuiltTxRequest {
    return this.buildPreparedTx(this.getRemoveDelegateEncoded());
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Core trading methods
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Open a new trade position.
   *
   * Call `approveUsdc()` (once) before the first trade — the contract will revert
   * on-chain if the USDC allowance is insufficient. Use `getBalances()` to check
   * the current allowance.
   */
  async openTrade(params: OpenTradeParams): Promise<SubmissionResult> {
    return this.submitPrepared(this.getOpenTradeEncoded(params));
  }

  getOpenTradeTx(params: OpenTradeParams): BuiltTxRequest {
    return this.buildPreparedTx(this.getOpenTradeEncoded(params));
  }

  private resolveOpenTradeBuilder(
    params: OpenTradeParams,
  ): { b: Address; f: number } | undefined {
    const override = params.builder;
    const address = override?.address ?? this.builderAddress;
    const feeBps = override?.feeBps ?? this.builderFeeBps;

    if (override?.feeBps !== undefined) {
      const feeError = validateBuilderFeeBps(override.feeBps);
      if (feeError) {
        throw new OstiumError(feeError, OstiumErrorCode.VALIDATION_FAILED);
      }
    }

    if (address === undefined || feeBps === undefined) {
      // feeBps: 0 with no resolvable address means "open without a builder fee".
      if (feeBps === 0) return undefined;
      if (override?.address !== undefined || override?.feeBps !== undefined) {
        throw new OstiumError(
          'openTrade builder override is missing address or feeBps — provide it on the trade or in client config.',
          OstiumErrorCode.VALIDATION_FAILED,
        );
      }
      return undefined;
    }

    return { b: address, f: feeBps };
  }

  private getOpenTradeEncoded(params: OpenTradeParams): EncodedTx {
    const collateralNum = parseFloat(params.collateral);
    if (collateralNum > MAX_COLLATERAL_USD) {
      throw new OstiumError(
        `Collateral ${params.collateral} exceeds maximum ${MAX_COLLATERAL_USD}`,
        OstiumErrorCode.VALIDATION_FAILED,
      );
    }

    const apiOpen = {
      a: Number(params.pairId),
      b: params.buy,
      p: params.price,
      s: params.collateral,
      l: params.leverage,
      t: params.type,
      tp: params.takeProfit,
      sl: params.stopLoss,
      d: params.isDayTrade,
    };

    const apiBuilder = this.resolveOpenTradeBuilder(params);

    let trade, orderType;
    try {
      ({ trade, orderType } = buildTrade(apiOpen, this.getTraderAddress()));
    } catch (err) {
      throw new OstiumError(
        `Trade validation failed: ${err instanceof Error ? err.message : String(err)}`,
        OstiumErrorCode.VALIDATION_FAILED,
        err,
      );
    }

    const builderFee = buildBuilderFee(apiBuilder);

    // Slippage only applies to market orders. Limit and stop orders must use 0.
    const slippageP = params.type === OrderType.Market
      ? BigInt(this.resolveSlippage(params.slippage))
      : 0n;

    return encodeOpenTrade(trade, builderFee, orderType, slippageP, this.contracts.trading);
  }

  /**
   * Close an open position (full or partial).
   *
   * `pairId` and `idx` come straight from a `Position` returned by
   * `getOpenPositions` — pass them through directly:
   *
   * ```ts
   * const { pairPositions } = await client.getOpenPositions();
   * const { pairId, idx } = pairPositions[0].position;
   * await client.closeTrade({ pairId, idx, price: '...', closePercent: 100 });
   * ```
   */
  async closeTrade(params: CloseTradeParams): Promise<SubmissionResult> {
    return this.submitPrepared(this.getCloseTradeEncoded(params));
  }

  getCloseTradeTx(params: CloseTradeParams): BuiltTxRequest {
    return this.buildPreparedTx(this.getCloseTradeEncoded(params));
  }

  private getCloseTradeEncoded(params: CloseTradeParams): EncodedTx {
    try {
      validateCloseParams({
        a: Number(params.pairId),
        t: params.idx,
        p: params.price,
        r: params.closePercent,
      });
    } catch (err) {
      throw new OstiumError(
        `Close validation failed: ${err instanceof Error ? err.message : String(err)}`,
        OstiumErrorCode.VALIDATION_FAILED,
        err,
      );
    }

    const pairIndex = BigInt(params.pairId);
    const index = BigInt(params.idx);
    // Contract expects close percentage × 100 (e.g. 100% → 10000)
    const closePercentage = BigInt(Math.round(params.closePercent * 100));
    const marketPrice = parsePrice(params.price);
    const slippageP = BigInt(this.resolveSlippage(params.slippage));

    return encodeCloseTradeMarket(
      pairIndex,
      index,
      closePercentage,
      marketPrice,
      slippageP,
      this.contracts.trading,
    );
  }

  /**
   * Cancel a pending order.
   *
   * TypeScript discriminates the params shape by `type` — required fields are
   * enforced at compile time.
   *
   * @example Cancel a pending limit order
   * ```ts
   * const [order] = await client.getOpenOrders();
   * await client.cancelOrder({ type: CancelOrderType.Limit, pairId: order.pairId, idx: order.idx });
   * ```
   * @example Cancel a timed-out market open
   * ```ts
   * await client.cancelOrder({ type: CancelOrderType.PendingOpen, orderId: 123 });
   * ```
   * @example Cancel a timed-out market close (retry: true re-submits the close)
   * ```ts
   * await client.cancelOrder({ type: CancelOrderType.PendingClose, orderId: 456, retry: true });
   * ```
   */
  async cancelOrder(params: CancelOrderParams): Promise<SubmissionResult> {
    return this.submitPrepared(this.getCancelOrderEncoded(params));
  }

  getCancelOrderTx(params: CancelOrderParams): BuiltTxRequest {
    return this.buildPreparedTx(this.getCancelOrderEncoded(params));
  }

  private getCancelOrderEncoded(params: CancelOrderParams): EncodedTx {
    let encoded: EncodedTx;

    if (params.type === CancelOrderType.Limit) {
      const pairIdNum = Number(params.pairId);
      const idxNum    = Number(params.idx);
      if (
        !Number.isFinite(pairIdNum) || !Number.isInteger(pairIdNum) || pairIdNum < 0 ||
        !Number.isFinite(idxNum)    || !Number.isInteger(idxNum)    || idxNum    < 0
      ) {
        throw new OstiumError(
          `Invalid pairId (${params.pairId}) or idx (${params.idx}) for limit cancel`,
          OstiumErrorCode.VALIDATION_FAILED,
        );
      }
      encoded = encodeCancelLimitOrder(
        BigInt(pairIdNum),
        BigInt(idxNum),
        this.contracts.trading,
      );
    } else if (params.type === CancelOrderType.PendingClose) {
      if (params.orderId < 0) {
        throw new OstiumError(
          `Invalid orderId (${params.orderId}) for pending close cancel`,
          OstiumErrorCode.VALIDATION_FAILED,
        );
      }
      encoded = encodeCloseTradeMarketTimeout(
        BigInt(params.orderId),
        params.retry ?? false,
        this.contracts.trading,
      );
    } else {
      if (params.orderId < 0) {
        throw new OstiumError(
          `Invalid orderId (${params.orderId}) for pending open cancel`,
          OstiumErrorCode.VALIDATION_FAILED,
        );
      }
      encoded = encodeOpenTradeMarketTimeout(BigInt(params.orderId), this.contracts.trading);
    }

    return encoded;
  }

  /**
   * Modify an open trade or a pending limit order.
   *
   * `pairId` and `idx` come straight from `getOpenPositions` (for TP/SL on open
   * positions) or `getOpenOrders` (for limit-order edits):
   *
   * ```ts
   * // Update TP/SL on an open position
   * const { pairPositions } = await client.getOpenPositions();
   * const { pairId, idx } = pairPositions[0].position;
   * await client.modifyOrder({ pairId, idx, takeProfit: '...', stopLoss: '...' });
   *
   * // Re-price a pending limit order
   * const [order] = await client.getOpenOrders();
   * await client.modifyOrder({ pairId: order.pairId, idx: order.idx, price: '...' });
   * ```
   *
   * - `price` provided → updates limit-order trigger price (with optional TP/SL).
   * - `takeProfit` only → calls updateTp on an open trade.
   * - `stopLoss` only → calls updateSl on an open trade.
   * - Both `takeProfit` and `stopLoss` without `price` → throws (send two calls).
   */
  async modifyOrder(params: ModifyOrderParams): Promise<SubmissionResult> {
    return this.submitPrepared(this.getModifyOrderEncoded(params));
  }

  getModifyOrderTx(params: ModifyOrderParams): BuiltTxRequest {
    return this.buildPreparedTx(this.getModifyOrderEncoded(params));
  }

  private getModifyOrderEncoded(params: ModifyOrderParams): EncodedTx {
    try {
      validateModifyParams({
        a: Number(params.pairId),
        i: params.idx,
        p: params.price,
        tp: params.takeProfit,
        sl: params.stopLoss,
      });
    } catch (err) {
      throw new OstiumError(
        `Modify validation failed: ${err instanceof Error ? err.message : String(err)}`,
        OstiumErrorCode.VALIDATION_FAILED,
        err,
      );
    }

    if (params.takeProfit && params.stopLoss && !params.price) {
      throw new OstiumError(
        'Cannot update both takeProfit and stopLoss simultaneously without a price. Send two separate modifyOrder calls.',
        OstiumErrorCode.VALIDATION_FAILED,
      );
    }

    const pairIndex = BigInt(params.pairId);
    const index = BigInt(params.idx);

    let encoded: EncodedTx;
    if (params.price) {
      const price = parsePrice(params.price);
      const tp = params.takeProfit ? parsePrice(params.takeProfit) : 0n;
      const sl = params.stopLoss ? parsePrice(params.stopLoss) : 0n;
      encoded = encodeUpdateOpenLimitOrder(pairIndex, index, price, tp, sl, this.contracts.trading);
    } else if (params.takeProfit) {
      encoded = encodeUpdateTp(pairIndex, index, parsePrice(params.takeProfit), this.contracts.trading);
    } else if (params.stopLoss) {
      encoded = encodeUpdateSl(pairIndex, index, parsePrice(params.stopLoss), this.contracts.trading);
    } else {
      throw new OstiumError(
        'Either takeProfit or stopLoss must be provided when price is not specified',
        OstiumErrorCode.VALIDATION_FAILED,
      );
    }

    return encoded;
  }

  /**
   * Update collateral on an open position (isolated margin).
   *
   * `pairId` and `idx` come straight from a `Position` returned by `getOpenPositions`:
   *
   * ```ts
   * const { pairPositions } = await client.getOpenPositions();
   * const { pairId, idx } = pairPositions[0].position;
   * await client.updateCollateral({ pairId, idx, amount: '50' });   // top up
   * await client.updateCollateral({ pairId, idx, amount: '-25' });  // remove
   * ```
   *
   * Positive amount → topUpCollateral (adds margin, reduces effective leverage).
   * Negative amount → removeCollateral (removes margin, increases effective leverage).
   *
   * Checks USDC allowance before top-up operations.
   */
  async updateCollateral(params: UpdateCollateralParams): Promise<SubmissionResult> {
    return this.submitPrepared(await this.getUpdateCollateralEncoded(params));
  }

  async getUpdateCollateralTx(params: UpdateCollateralParams): Promise<BuiltTxRequest> {
    return this.buildPreparedTx(await this.getUpdateCollateralEncoded(params));
  }

  private async getUpdateCollateralEncoded(params: UpdateCollateralParams): Promise<EncodedTx> {
    const amount = parseFloat(params.amount);
    if (Number.isNaN(amount) || amount === 0) {
      throw new OstiumError(
        `Invalid collateral amount: ${params.amount}`,
        OstiumErrorCode.VALIDATION_FAILED,
      );
    }

    const pairIndex = BigInt(params.pairId);
    const index = BigInt(params.idx);
    const absAmount = parseUsdc(Math.abs(amount).toString());

    let encoded: EncodedTx;
    if (amount > 0) {
      const allowance = await this.checkUsdcAllowance(Math.abs(amount).toString());
      if (!allowance.sufficient) {
        throw new OstiumError(
          `Insufficient USDC allowance for collateral top-up. Current: ${allowance.current}, Required: ${allowance.required}. Call approveUsdc() first.`,
          OstiumErrorCode.ALLOWANCE_INSUFFICIENT,
        );
      }
      encoded = encodeTopUpCollateral(pairIndex, index, absAmount, this.contracts.trading);
    } else {
      encoded = encodeRemoveCollateral(pairIndex, index, absAmount, this.contracts.trading);
    }

    return encoded;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Subgraph reads (Hyperliquid-style API)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * All trading pairs with computed `minSz`/`maxBSz`/`maxSSz`, live prices, and
   * market-status flags. Pass `pairIds` to restrict to a subset.
   */
  getPairs(params?: GetPairsParams): Promise<PairsResponse> {
    return this.subgraph.getPairs({
      ...params,
      builderFeeBps: params?.builderFeeBps ?? this.builderFeeBps ?? 0,
    });
  }

  /** Live mid/bid/ask prices keyed by `pairId`. */
  getAllPrices(): Promise<AllPricesResponse> {
    return this.subgraph.getAllPrices();
  }

  /**
   * Open positions + margin summary for a user. Defaults to the connected
   * trader. Live prices and the current block number are fetched automatically.
   *
   * - `user`: pass an address to scope to a single trader, or `'ALL'` to fetch
   *   positions across every trader (no trader filter).
   * - `limit`: cap the number of positions returned (default: all).
   * - `skip`: offset into the result set for pagination (default: `0`).
   */
  async getOpenPositions(params?: Partial<GetOpenPositionsParams>): Promise<OpenPositionsResponse> {
    return this.subgraph.getOpenPositions({
      user:        params?.user        ?? this.getTraderAddress(),
      blockNumber: params?.blockNumber ?? (await this.publicClient.getBlockNumber()),
      limit:       params?.limit,
      skip:        params?.skip,
    });
  }

  /**
   * Executed fills, newest first.
   *
   * - `user` defaults to the connected trader. Pass `'ALL'` to fetch fills
   *   across every trader (no trader filter).
   * - `pairId` (optional) — restricts to a single pair.
   * - `limit` (optional, default `1000`) — caps the result. Pass `Infinity` to
   *   fetch every matching fill.
   */
  getFills(params?: Partial<GetFillsParams>): Promise<Fill[]> {
    return this.subgraph.getFills({
      user:   params?.user   ?? this.getTraderAddress(),
      pairId: params?.pairId,
      limit:  params?.limit,
    });
  }

  /**
   * Executed fills within a time range (Unix ms). Same filters as `getFills`,
   * including the default `limit: 1000`.
   */
  getFillsByTime(params: Partial<GetFillsByTimeParams> & { startTime: number }): Promise<Fill[]> {
    return this.subgraph.getFillsByTime({
      user:      params.user ?? this.getTraderAddress(),
      pairId:    params.pairId,
      limit:     params.limit,
      startTime: params.startTime,
      endTime:   params.endTime,
    });
  }

  /** Active limit orders for a user. Defaults to the connected trader. */
  getOpenOrders(params?: Partial<GetUserParams>): Promise<OpenOrder[]> {
    return this.subgraph.getOpenOrders({ user: params?.user ?? this.getTraderAddress() });
  }

  /**
   * Fetch orders at any status — pending, executed, or cancelled.
   *
   * Pass `initiatedTxHashes: [result.txHash]` to poll by the submission hash, or
   * `orderIds` by on-chain id. Without either, returns recent orders for the
   * connected trader unless `user: 'ALL'` or `builder` is provided.
   */
  getOrders(params?: GetOrdersParams): Promise<Order[]> {
    const byId = params?.orderIds?.length;
    const byTx = params?.initiatedTxHashes?.length;
    const hasBuilder = !!params?.builder;
    const shouldDefaultUser = !byId && !byTx && !hasBuilder;
    return this.subgraph.getOrders({
      ...params,
      user: params?.user ?? (shouldDefaultUser ? this.getTraderAddress() : undefined),
    });
  }

  /**
   * Fetch orders routed through a builder, including sibling close orders on the
   * same positions. See {@link OstiumSubgraphClient.getBuilderOrders}.
   */
  getBuilderOrders(builder: Address, params?: GetBuilderOrdersParams): Promise<Order[]> {
    return this.subgraph.getBuilderOrders(builder, params);
  }

  /**
   * Simulate per-side slippage for a list of pairs and notionals.
   * See {@link OstiumSubgraphClient.getSimSlippage} for full semantics.
   */
  getSimSlippage(params: GetSimSlippageParams): Promise<SimSlippageByPairId> {
    return this.subgraph.getSimSlippage(params);
  }

  /**
   * Build a synthetic bid/ask orderbook for one pair using exponentially-spaced
   * notional levels capped at each side's remaining OI capacity.
   * See {@link OstiumSubgraphClient.getSimOrderbook} for full semantics.
   */
  getSimOrderbook(params: GetSimOrderbookParams): Promise<SimOrderbookResponse> {
    return this.subgraph.getSimOrderbook(params);
  }

  /**
   * Preview a trade before signing it — execution price after spread, fees,
   * resulting liquidation price, and the checks that would revert on-chain.
   * See {@link OstiumSubgraphClient.previewOpenTrade} for full semantics.
   *
   * ```ts
   * const preview = await client.previewOpenTrade({
   *   pairId: 0, isLong: true, collateral: 100, leverage: 10,
   * });
   * if (!preview.isValid) console.warn(preview.warnings);
   * ```
   */
  previewOpenTrade(params: PreviewOpenTradeParams): Promise<PreviewOpenTradeResult> {
    // Default to the client's configured builder fee so a builder-integrated
    // ticket quotes the fee the trade will actually pay. Pass `builderFeeBps`
    // explicitly to override, including `0` to preview without one.
    return this.subgraph.previewOpenTrade({
      builderFeeBps: this.builderFeeBps,
      ...params,
    });
  }

  /**
   * Protocol vault balance in USDC — the liquidity backing trader PnL.
   * See {@link OstiumSubgraphClient.getVaultBalance}.
   */
  getVaultBalance(): Promise<number> {
    return this.subgraph.getVaultBalance();
  }

  /**
   * Fetch OHLC candles for a pair from the builder API.
   *
   * `from` / `to` are Unix milliseconds; `resolution` is one of
   * `"1"`, `"5"`, `"15"`, `"60"`, `"240"`, `"1D"`. Pass `sets` to fetch
   * multiple candle pages and return them as one flattened array.
   *
   * ```ts
   * const candles = await client.getCandles({
   *   pairId: 0,
   *   from: Date.now() - 30 * 86_400_000,
   *   resolution: '1D',
   *   sets: 3,
   * });
   * ```
   */
  getCandles(params: GetCandlesParams): Promise<Candle[]> {
    return this.subgraph.getCandles(params);
  }

  /**
   * Open a WebSocket connection to the live price stream.
   *
   * Optionally filter to a subset of pairs by `pairId` — same values as returned by
   * `getPairs()`. When provided, the client sends `{ type: "subscribe", pairs }` on
   * `open` (no `?pairs=` query on the URL). Omit `pairIds` to receive the full feed.
   *
   * ```ts
   * const stream = client.streamPrices([0, 1]);  // BTC and ETH by pairId
   * stream.onSnapshot(ticks => console.log(ticks));
   * stream.onTick(tick => console.log(tick.pair, tick.mid));
   * stream.subscribe([2]);    // add pair
   * stream.unsubscribe([0]);  // remove pair
   * stream.close();
   * ```
   *
   * Requires Node.js 18+, Bun, or a browser environment. Bun’s `WebSocket` may fail
   * some `https://` upgrades; use Node if the connection never reaches `open`.
   */
  streamPrices(pairIds?: Array<string | number>): OstiumPriceStream {
    return this.subgraph.streamPrices(pairIds);
  }

  /**
   * Stream price-driven updates for an existing `getOpenPositions()` response.
   *
   * Subscribes only to the unique pairs referenced by the response and emits the
   * full updated positions payload on each relevant price update. Pass an
   * existing `priceStream` to reuse a websocket your app already owns, or omit
   * it to let the SDK create a dedicated connection.
   */
  streamPositionUpdates(
    initial: OpenPositionsResponse,
    priceStream?: OstiumPriceStream,
  ): OstiumPositionUpdatesStream {
    return this.subgraph.streamPositionUpdates(initial, priceStream);
  }

  /**
   * Stream account confirmation snapshots for one or more traders.
   *
   * Emits full snapshots containing pending market orders, active limit/stop
   * orders, and open trades. Uses subgraph polling plus Alchemy contract logs so
   * market opens and full closes show up before subgraph indexing completes.
   *
   * Pass one or more addresses in `user` to subscribe to multiple accounts on a single
   * stream; defaults to the connected trader. Emitted snapshots are keyed by
   * normalized trader address, with `{ positions, orders, limits }` per trader.
   */
  streamAccountUpdates(params: StreamAccountUpdatesParams = {}): OstiumAccountUpdatesStream {
    return this.subgraph.streamAccountUpdates({
      ...params,
      user: params.user ?? [this.getTraderAddress()],
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────────────────

  private getSetupGaslessDelegationEncoded(): EncodedTx {
    if (!this.selfGasless || !this.effectiveSender) {
      throw new OstiumError(
        'setupGaslessDelegation() is only available in Self + Gasless mode.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    return encodeSetDelegate(this.effectiveSender, this.contracts.trading);
  }

  private getApproveUsdcEncoded(amount: string): EncodedTx {
    if (this.delegated) {
      throw new OstiumError(
        'approveUsdc is not available in delegated mode. The trader must approve USDC directly from their own account.',
        OstiumErrorCode.DELEGATION_FAILED,
      );
    }
    const rawAmount = amount === 'max' ? maxUint256 : parseUsdc(amount);
    return encodeUsdcApprove(this.contracts.tradingStorage, rawAmount, this.contracts.usdc);
  }

  private getSetDelegateEncoded(delegateAddress: Address): EncodedTx {
    return encodeSetDelegate(delegateAddress, this.contracts.trading);
  }

  private getRemoveDelegateEncoded(): EncodedTx {
    return encodeRemoveDelegate(this.contracts.trading);
  }

  private prepareEncoded(encoded: EncodedTx): EncodedTx {
    const signer = this.requireBuildCapability();
    return signer.prepare(encoded);
  }

  private buildPreparedTx(encoded: EncodedTx): BuiltTxRequest {
    return this.toBuiltTxRequest(this.prepareEncoded(encoded));
  }

  private buildDirectEoaTx(encoded: EncodedTx): BuiltTxRequest {
    if (!this.canBuildTransactions() || !this.traderAddress) {
      throw new OstiumError(
        'This client cannot build transactions. Use one of the createSelfAnd*/createDelegatedAnd* factories.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    return {
      kind: 'eoa',
      to: encoded.to,
      data: encoded.data,
      value: encoded.value,
      from: this.traderAddress,
      traderAddress: this.traderAddress,
    };
  }

  private toBuiltTxRequest(encoded: EncodedTx): BuiltTxRequest {
    if (!this.canBuildTransactions() || !this.traderAddress || !this.effectiveSender) {
      throw new OstiumError(
        'This client cannot build transactions. Use one of the createSelfAnd*/createDelegatedAnd* factories.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }

    if (this.gasless) {
      return {
        kind: 'safe',
        safeAddress: this.effectiveSender,
        traderAddress: this.traderAddress,
        calls: [encoded],
      };
    }

    return {
      kind: 'eoa',
      to: encoded.to,
      data: encoded.data,
      value: encoded.value,
      from: this.effectiveSender,
      traderAddress: this.traderAddress,
    };
  }

  private async submitPrepared(encoded: EncodedTx): Promise<SubmissionResult> {
    const submitter = this.requireSubmitCapability();
    const isTradingAction = encoded.to.toLowerCase() === this.contracts.trading.toLowerCase();
    const result = await submitter.submit(this.prepareEncoded(encoded));
    if (isTradingAction) reportTradeSubmission(this.builderApiUrl, result.txHash);
    return result;
  }

  /** Direct EOA submission — used only in Self + Gasless for approveUsdc and setupGaslessDelegation. */
  private async submitDirectEoa(encoded: EncodedTx): Promise<SubmissionResult> {
    if (!this.eoaSubmit) {
      throw new OstiumError(
        'This client cannot submit direct EOA transactions.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    try {
      return await this.eoaSubmit(encoded);
    } catch (err) {
      if (err instanceof OstiumError) throw err;
      const msg = err instanceof Error ? err.message : String(err);
      const lower = msg.toLowerCase();
      if (lower.includes('revert')) {
        throw new OstiumError(`Contract reverted: ${msg}`, OstiumErrorCode.CONTRACT_ERROR, err);
      }
      if (lower.includes('network') || lower.includes('timeout') || lower.includes('fetch')) {
        throw new OstiumError(`Network error: ${msg}`, OstiumErrorCode.NETWORK_ERROR, err);
      }
      throw new OstiumError(`Transaction failed: ${msg}`, OstiumErrorCode.SUBMISSION_FAILED, err);
    }
  }

  private requireBuildCapability(): SignerStrategy {
    if (!this.signer) {
      throw new OstiumError(
        'This client cannot build transactions. Use one of the createSelfAnd*/createDelegatedAnd* factories.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    return this.signer;
  }

  private requireSubmitCapability(): SubmissionStrategy {
    if (!this.submitter) {
      throw new OstiumError(
        'This client does not have submission credentials. Use the corresponding get*Tx() method or initialize with a private key.',
        OstiumErrorCode.INVALID_CONFIG,
      );
    }
    return this.submitter;
  }

  private resolveSlippage(override?: number): number {
    return override ?? this.defaultSlippageBps;
  }
}
